import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaStar, FaClock, FaMapMarkerAlt, FaCalendarAlt, FaMountain,
  FaCheckCircle, FaWhatsapp, FaPhone, FaArrowLeft, FaInfoCircle,
  FaRoute, FaCampground, FaLightbulb, FaList
} from 'react-icons/fa';
import destinationsData from '../data/destinations.json';
import Navbar from './Navbar';
import Footer from './Footer';

const DestinationDetail = () => {
  const { id } = useParams();
  const [destination, setDestination] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const destData = destinationsData.find(d => d.id === id);
    if (destData) {
      setDestination(destData);
      setSelectedImage(0);
    }
  }, [id]);

  if (!destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-800 mb-4">Destination not found</h2>
          <Link to="/" className="text-accent-600 hover:text-accent-700 font-semibold">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleBookTrip = () => {
    const message = `Hello! I'm interested in planning a trip to ${destination.name}.%0A%0ADuration: ${destination.duration}%0ABest Season: ${destination.bestSeason}%0A%0APlease help me plan this journey.`;
    window.open(`https://wa.me/918380866111?text=${message}`, '_blank');
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-md py-6 sticky top-0 z-40">
        <div className="container-custom flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-primary-700 hover:text-accent-600 transition-colors">
            <FaArrowLeft />
            <span className="font-semibold">Back to Home</span>
          </Link>
          <div className="flex items-center space-x-4">
            <a href="tel:+918380866111" className="hidden sm:flex items-center space-x-2 text-primary-700 hover:text-accent-600 transition-colors">
              <FaPhone />
              <span className="font-semibold">+91 83808-66111</span>
            </a>
            <button
              onClick={handleBookTrip}
              className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition-colors flex items-center space-x-2"
            >
              <FaWhatsapp />
              <span className="font-semibold">Plan This Trip</span>
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Title Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-primary-600 mb-2">
            <Link to="/" className="hover:text-accent-600">Home</Link>
            <span>/</span>
            <Link to="/#destinations" className="hover:text-accent-600">Destinations</Link>
            <span>/</span>
            <span className="text-accent-600">{destination.name}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">{destination.name}</h1>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className={`${i < Math.floor(destination.rating) ? 'text-accent-500' : 'text-gray-300'}`} />
              ))}
              <span className="ml-2 text-primary-700 font-semibold">{destination.rating}</span>
              <span className="text-primary-600">({destination.reviews} reviews)</span>
            </div>
            <span className="px-4 py-1 bg-primary-100 text-primary-700 rounded-full font-semibold">{destination.region}</span>
            <span className={`px-4 py-1 rounded-full font-semibold ${
              destination.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
              destination.difficulty === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {destination.difficulty}
            </span>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="mb-12">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-4">
            <img
              src={destination.images[selectedImage]}
              alt={destination.name}
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute bottom-4 right-4 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
              {selectedImage + 1} / {destination.images.length}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {destination.images.map((img, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`rounded-xl overflow-hidden ${selectedImage === index ? 'ring-4 ring-accent-500' : 'opacity-70 hover:opacity-100'} transition-all`}
              >
                <img src={img} alt={`${destination.name} ${index + 1}`} className="w-full h-24 object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Video Section */}
        {destination.videoUrl && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-primary-800 mb-6">Destination Preview</h2>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <iframe
                width="100%"
                height="500"
                src={destination.videoUrl}
                title={`${destination.name} Preview`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full"
              ></iframe>
            </div>
          </div>
        )}

        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaClock className="text-4xl text-accent-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Duration</p>
            <p className="text-xl font-bold text-primary-800">{destination.duration}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaMapMarkerAlt className="text-4xl text-primary-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Distance</p>
            <p className="text-xl font-bold text-primary-800">{destination.distance}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaCalendarAlt className="text-4xl text-forest-500 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Best Season</p>
            <p className="text-xl font-bold text-primary-800">{destination.bestSeason}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <FaMountain className="text-4xl text-accent-600 mx-auto mb-3" />
            <p className="text-primary-600 text-sm mb-1">Difficulty</p>
            <p className="text-xl font-bold text-primary-800">{destination.difficulty}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b-2 border-primary-200">
            {[
              { id: 'overview', label: 'Overview', icon: <FaInfoCircle /> },
              { id: 'itinerary', label: 'Itinerary', icon: <FaList /> },
              { id: 'camping', label: 'Camping Spots', icon: <FaCampground /> },
              { id: 'tips', label: 'Tips', icon: <FaLightbulb /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-semibold transition-colors flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'text-accent-600 border-b-4 border-accent-600 -mb-0.5'
                    : 'text-primary-600 hover:text-accent-600'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-3xl p-8 shadow-lg mb-12">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">About This Journey</h2>
              <p className="text-lg text-primary-600 mb-8 leading-relaxed">{destination.description}</p>
              
              <h3 className="text-2xl font-bold text-primary-800 mb-4">Highlights</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {destination.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl">
                    <FaCheckCircle className="text-accent-500 mt-1 flex-shrink-0" />
                    <span className="text-primary-700">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'itinerary' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Suggested Itinerary</h2>
              <div className="space-y-6">
                {destination.itinerary.map((day, index) => (
                  <div key={index} className="relative pl-8 pb-8 border-l-4 border-accent-500 last:border-0">
                    <div className="absolute -left-4 top-0 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center text-white font-bold">
                      {day.day}
                    </div>
                    <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-2xl shadow-md">
                      <h4 className="text-xl font-bold text-primary-800 mb-2">{day.title}</h4>
                      <p className="text-primary-600 mb-4">{day.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {day.activities.map((activity, idx) => (
                          <span key={idx} className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-semibold">
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'camping' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Recommended Camping Spots</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {destination.campingSpots.map((spot, index) => (
                  <div key={index} className="bg-gradient-to-br from-forest-50 to-primary-50 p-6 rounded-2xl shadow-lg border-2 border-forest-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-primary-800 mb-2">{spot.name}</h4>
                        <p className="text-accent-600 font-bold text-lg">{spot.price}</p>
                      </div>
                      <FaCampground className="text-3xl text-forest-600" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-primary-700 font-semibold mb-2">Facilities:</p>
                      {spot.facilities.map((facility, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <FaCheckCircle className="text-green-500 text-sm" />
                          <span className="text-primary-600">{facility}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'tips' && (
            <div>
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Travel Tips & Advice</h2>
              <div className="space-y-4 mb-8">
                {destination.tips.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-xl border-l-4 border-yellow-400">
                    <FaLightbulb className="text-yellow-500 text-xl mt-1 flex-shrink-0" />
                    <p className="text-primary-700">{tip}</p>
                  </div>
                ))}
              </div>

              <h3 className="text-2xl font-bold text-primary-800 mb-4">What's Included in Our Service</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {destination.included.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                    <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-primary-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-600 to-forest-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Ready for This Adventure?</h2>
          <p className="text-xl mb-8 text-primary-100">Let us help you plan the perfect trip to {destination.name}!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleBookTrip}
              className="bg-green-500 text-white px-8 py-4 rounded-full hover:bg-green-600 transition-colors flex items-center justify-center space-x-2 text-lg font-bold shadow-xl"
            >
              <FaWhatsapp className="text-2xl" />
              <span>Plan via WhatsApp</span>
            </button>
            <a
              href="tel:+918380866111"
              className="bg-white text-primary-700 px-8 py-4 rounded-full hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2 text-lg font-bold shadow-xl"
            >
              <FaPhone />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </div>

      </div>
      <Footer />
    </>
  );
};

export default DestinationDetail;
